import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { sortBy, truncate } from 'lodash';
import { CodeNameModel } from 'components/common/types';
import { InputSize } from 'components/common/types/Input.types';
import { NavigationItem } from 'components/common/types/NavigationItem.types';
import { PoiOption } from 'components/common/types/Planner.types';
import { calculateSecondaryPanelPosition } from 'components/pages/Planner/PlannerLineActions/calculateSecondaryPanelPosition';
import PlannerSecondaryPanel from 'components/pages/Planner/PlannerSecondaryPanel';
import { Transition } from 'consts/transition';
import BaseSelect from 'lib/BaseSelect';
import { Label } from 'lib/Label';
import Alert from 'lib/Alert';
import Search from 'lib/Search';
import { AlertThemeOption } from 'lib/Alert/Alert.types';
import searchOptions from 'utils/searchOptions';
import { POIList } from './POIList';
import { POIPanelProps } from './PointsOfInterest.types';
import { usePOIData } from './usePOIData';
import { OpenStreetMapPoiSearch } from '../ProgrammaticPointsOfInterest/OpenStreetMapPoiSearch/OpenStreetMapPoiSearch';
import { OPEN_STREET_MAP_DATA_PROVIDER } from '../ProgrammaticPointsOfInterest/OpenStreetMapPoiSearch/consts';
import { filterSelectedPoiOptions } from './utils';
import { CustomPOIUpload, CustomPOICategoryList } from './CustomPOIUpload';
import { usePOI } from './usePOI';

const SECONDARY_PANEL_WIDTH = 400;

const POIPanel: React.FC<POIPanelProps> = ({
  onClose,
  cancelButtonLabel,
  confirmButtonLabel,
  isSubsection,
  selectedPOIs,
  selectedFilterPOIs,
  onConfirm,
  hideParentIncludeExclude,
  numberOfAllowedPOIs,
  isOpenStreetMapPOIEnabled,
  isCustomPOIUploadEnabled = true,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const [selectedProvider, setSelectedProvider] = useState('');
  const [navigationStack, setNavigationStack] = useState<NavigationItem[]>([]);
  const [transition, setTransition] = useState<Transition>(Transition.NONE);

  const { clearSearchResults, searchPoi, searchTerm, dataProvidersWithPoiOptions } = usePOIData();

  const {
    currentFocusedItemIndex,
    getSelectedPOIIncludeAndRadius,
    handleConfirm,
    isItemSelected,
    isSelectedPOIChanged,
    onChangeRadius,
    onIncludeExcludeChange,
    resetCurrentFocusedItemIndex,
    resetPOIToPreviousState,
    getSelectedPOIIncludedExcludedChildrenCounter,
    isNumberOfPOIsExceeded,
    isAnyRadiusOfPOIZero,
    customPoiCategories,
    onFileChange,
    onDeleteCustomCategory,
    hasReachedFileUploadLimit,
    isCustomCategory,
    isInsideCustomCategory,
  } = usePOI({
    pois: selectedPOIs,
    selectedProvider,
    onConfirm,
    numberOfAllowedPOIs,
    navigationStack,
  });

  const dataProviders = useMemo(() => {
    const dataProviderOptions = Object.keys(dataProvidersWithPoiOptions ?? {});

    if (isOpenStreetMapPOIEnabled) {
      dataProviderOptions.push(OPEN_STREET_MAP_DATA_PROVIDER.label);
    }

    return dataProviderOptions;
  }, [dataProvidersWithPoiOptions, isOpenStreetMapPOIEnabled]);
  const currentScreen = navigationStack[navigationStack.length - 1];
  const isMainLevel = navigationStack.length === 0;

  useEffect(() => {
    if (dataProviders.length === 0) return;
    if (dataProviders.length === 1) setSelectedProvider(dataProviders[0]);

    const firstDataProviderInSortedList = sortBy(dataProviders)[0];
    setSelectedProvider(firstDataProviderInSortedList);
  }, [dataProviders]);

  useEffect(() => {
    resetCurrentFocusedItemIndex();
  }, [searchTerm]);

  const handleSearchChange = (value: string): void => {
    searchPoi({
      text: value ?? '',
      dataProvider: selectedProvider,
    });
  };

  const filteredCustomPoiCategories = useMemo(
    () => (searchTerm ? searchOptions(customPoiCategories, searchTerm) : customPoiCategories),
    [customPoiCategories, searchTerm],
  );

  const navigablePoiOptions: PoiOption[] = useMemo(() => {
    if (!selectedProvider) return [];

    let initialOptions: PoiOption[] = [];

    if (isSubsection) {
      initialOptions = filterSelectedPoiOptions({
        poiOptions: dataProvidersWithPoiOptions[selectedProvider] || [],
        selectedPOIs,
        selectedFilterPOIs: selectedFilterPOIs ?? [],
      });
    } else {
      initialOptions = dataProvidersWithPoiOptions[selectedProvider] || [];
    }

    if (filteredCustomPoiCategories.length > 0) {
      initialOptions = [...filteredCustomPoiCategories, ...initialOptions];
    }

    return navigationStack.reduce(
      (options, navItem) => options.find((option) => option.name === navItem.title)?.children || [],
      initialOptions,
    );
  }, [
    isSubsection,
    filterSelectedPoiOptions,
    selectedProvider,
    dataProvidersWithPoiOptions,
    navigationStack,
    filteredCustomPoiCategories,
  ]);

  const displayedPoiOptions = useMemo(() => {
    if (!isMainLevel) return navigablePoiOptions;
    return navigablePoiOptions.filter((option) => !isCustomCategory(option));
  }, [navigablePoiOptions, isMainLevel, isCustomCategory]);

  const handleProviderSelect = (_: string, value: CodeNameModel): void => {
    setSelectedProvider(value.name);
    setNavigationStack([]);
    clearSearchResults();
  };

  const navigateForward = (title: string, categoryPath?: string): void => {
    setTransition(Transition.FORWARD);
    setNavigationStack((prev: NavigationItem[]) => [...prev, { title, categoryPath }]);
  };

  const navigateBack = (): void => {
    setTransition(Transition.BACKWARD);
    setNavigationStack(navigationStack.slice(0, -1));
  };

  const onTransitionComplete = (): void => {
    setTransition(Transition.NONE);
  };

  const handleCancel = (): void => {
    if (!isMainLevel) {
      navigateBack();

      return;
    }

    if (!isSubsection) resetPOIToPreviousState();

    onClose();
  };

  const isOpenStreetMap = selectedProvider === OPEN_STREET_MAP_DATA_PROVIDER.label;

  const searchComponent = (
    <div className="mb-2">
      {isOpenStreetMap ? (
        <OpenStreetMapPoiSearch onIncludeExcludeChange={onIncludeExcludeChange} onChangeRadius={onChangeRadius} />
      ) : (
        <Search size={InputSize.MEDIUM} value={searchTerm} placeholder="Search POI" onChange={handleSearchChange} />
      )}
    </div>
  );

  const dataProviderOptions = useMemo(
    () => dataProviders.map((provider) => ({ code: provider, name: provider })),
    [dataProviders],
  );

  const dataProviderSelect = (
    <div
      className={classNames('transition-all delay-300 duration-300 ease-in-out', {
        'opacity-100 my-0 mb-2': isMainLevel,
        'opacity-0 invisible -my-[63px] mb-0': !isMainLevel,
      })}
    >
      <Label label="Data provider" />
      <BaseSelect
        dataTestId="poi-data-providers"
        name="data providers"
        options={dataProviderOptions}
        selectedValue={{ code: selectedProvider, name: selectedProvider }}
        onSelect={handleProviderSelect}
      />
    </div>
  );

  return (
    <PlannerSecondaryPanel
      title={currentScreen ? truncate(`Select in ${currentScreen.title}`, { length: 50, omission: '...' }) : 'POI'}
      cancelButtonLabel={isMainLevel ? cancelButtonLabel : 'Back'}
      confirmButtonLabel={confirmButtonLabel}
      customStyles={calculateSecondaryPanelPosition({ width: SECONDARY_PANEL_WIDTH })}
      onCancel={handleCancel}
      onSubmit={handleConfirm}
      isDisabled={!isSelectedPOIChanged || isNumberOfPOIsExceeded || isAnyRadiusOfPOIZero}
      showBackButton={!isMainLevel || isSubsection}
      isSubsection={isSubsection}
      isMainLevel={isMainLevel}
      isChange={isSelectedPOIChanged}
    >
      <div className="flex flex-col flex-1 h-full">
        {isNumberOfPOIsExceeded ? (
          <Alert
            header="Max POI category"
            description={`POI has a maximum limit of up to ${numberOfAllowedPOIs} categories per deal.`}
            theme={AlertThemeOption.LIGHT_PRIMARY}
            customClassName="!p-5 mb-2"
          />
        ) : null}
        {searchComponent}
        {isCustomPOIUploadEnabled && (
          <div
            className={classNames('transition-all delay-300 duration-300 ease-in-out overflow-hidden', {
              'opacity-100 max-h-[500px]': isMainLevel,
              'opacity-0 max-h-0': !isMainLevel,
            })}
          >
            <CustomPOIUpload onFileChange={onFileChange} hasReachedFileUploadLimit={hasReachedFileUploadLimit} />
            <CustomPOICategoryList
              customPoiCategories={filteredCustomPoiCategories}
              isItemSelected={isItemSelected}
              getSelectedPOIIncludeAndRadius={getSelectedPOIIncludeAndRadius}
              getSelectedPOIIncludedExcludedChildrenCounter={getSelectedPOIIncludedExcludedChildrenCounter}
              navigateForward={navigateForward}
              onChangeRadius={onChangeRadius}
              onIncludeExcludeChange={onIncludeExcludeChange}
              resetCurrentFocusedItemIndex={resetCurrentFocusedItemIndex}
              onDeleteCustomCategory={onDeleteCustomCategory}
            />
          </div>
        )}
        {dataProviderSelect}
        {searchTerm.length > 0 && displayedPoiOptions.length === 0 && filteredCustomPoiCategories.length === 0 ? (
          <div>There are no results matching your search</div>
        ) : (
          selectedProvider && (
            <POIList
              currentFocusedItemIndex={currentFocusedItemIndex}
              getSelectedPOIIncludeAndRadius={getSelectedPOIIncludeAndRadius}
              getSelectedPOIIncludedExcludedChildrenCounter={getSelectedPOIIncludedExcludedChildrenCounter}
              isItemSelected={isItemSelected}
              onChangeRadius={onChangeRadius}
              onIncludeExcludeChange={onIncludeExcludeChange}
              resetCurrentFocusedItemIndex={resetCurrentFocusedItemIndex}
              poiOptions={displayedPoiOptions}
              navigateForward={navigateForward}
              onTransitionComplete={onTransitionComplete}
              isMainLevel={isMainLevel}
              transition={transition}
              selectedProvider={selectedProvider}
              hideParentIncludeExclude={hideParentIncludeExclude}
              isDeselectAllowed={!isInsideCustomCategory}
            />
          )
        )}
      </div>
    </PlannerSecondaryPanel>
  );
};

export default POIPanel;
